import { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const APP_VERSION = '2.1.1'; // Force rebuild

// Translations
const translations = {
  en: {
    home: 'Home',
    movies: 'Movies',
    series: 'TV Series',
    liveStreams: 'Live Streams',
    community: 'Community',
    clubs: 'CineClubs',
    login: 'Login',
    signUp: 'Sign Up',
    logout: 'Logout',
    profile: 'Profile',
    settings: 'Settings',
    watchParty: 'Watch Party',
    myClub: 'My Club',
    myStreamRoom: 'My Stream Room',
    adminPanel: 'Admin Panel',
    search: 'Search movies...',
    notifications: 'Notifications',
    noNotifications: 'No notifications yet',
    markAllRead: 'Mark all read',
    stayUpdated: 'Stay updated with your club activities',
    callEnded: 'Call Ended',
    language: 'Language',
    subscribe: 'Subscribe',
  },
  ru: {
    home: 'Главная',
    movies: 'Фильмы',
    series: 'Сериалы',
    liveStreams: 'Трансляции',
    community: 'Сообщество',
    clubs: 'Клубы',
    login: 'Войти',
    signUp: 'Регистрация',
    logout: 'Выйти',
    profile: 'Профиль',
    settings: 'Настройки',
    watchParty: 'Смотреть вместе',
    myClub: 'Мой клуб',
    myStreamRoom: 'Моя трансляция',
    adminPanel: 'Админ панель',
    search: 'Поиск фильмов...',
    notifications: 'Уведомления',
    noNotifications: 'Уведомлений пока нет',
    markAllRead: 'Прочитать все',
    stayUpdated: 'Следите за активностью клуба',
    callEnded: 'Звонок завершён',
    language: 'Язык',
    subscribe: 'Подписаться',
  }
};

// Get language from cookie
const getLanguage = () => {
  const match = document.cookie.match(/(?:^|; )lang=([^;]*)/);
  return match ? match[1] : 'en';
};

// Set language
const setLanguagePreference = async (lang) => {
  document.cookie = `lang=${lang}; path=/; max-age=${365*24*60*60}; SameSite=Lax`;
  try {
    await fetch('/api/language.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: lang })
    });
  } catch (e) {
    console.error('Failed to set language:', e);
  }
  window.location.reload();
};

const UniversalNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [coins, setCoins] = useState(0);
  const [userClub, setUserClub] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [processingActions, setProcessingActions] = useState(new Set());
  const [lang, setLang] = useState(getLanguage());
  const [showLangMenu, setShowLangMenu] = useState(false);
  const location = useLocation();
  
  const t = translations[lang] || translations.en;
  
  // Get current pathname - works for both React Router and PHP pages
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : location.pathname;
  
  // Check if current page is active
  const isActive = (path) => {
    if (path === '/live/streams') {
      return currentPath.startsWith('/live');
    }
    return currentPath.startsWith(path);
  };
  
  const activeClass = 'text-red-500 font-bold bg-red-600/20 px-3 py-1.5 rounded-lg';

  // Inactivity auto-logout removed - session managed by auto-logout.js (5 hours browser close timeout)

  useEffect(() => {
    checkAuth();
    loadNotifications();
    loadUserClub();
    loadUserSubscription();
    
    // Poll notifications every 10 seconds for real-time updates
    const notificationPoll = setInterval(() => {
      loadNotifications();
    }, 10000);
    
    return () => {
      clearInterval(notificationPoll);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu-container') && showUserMenu) {
        setShowUserMenu(false);
      }
      if (!event.target.closest('.notifications-container') && showNotifications) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, showNotifications]);

  // Disable body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reload club membership when location changes
  useEffect(() => {
    if (user) {
      loadUserClub();
    }
  }, [location, user]);

  // Search movies
  useEffect(() => {
    const searchMovies = async () => {
      if (search.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await fetch(`/api/movies-search.php?q=${encodeURIComponent(search.trim())}`);
        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          setSearchResults(data.data.slice(0, 5)); // Show top 5
          setShowSearchResults(true);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(searchMovies, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    
    // If no token, clear cached user and stay logged out
    if (!token) {
      localStorage.removeItem('user');
      setUser(null);
      return;
    }
    
    // Only call API if we have a token
    try {
      const headers = {};
      if (token !== 'session_active') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/auth/me', {
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data) {
          setUser(data.data);
          localStorage.setItem('user', JSON.stringify(data.data));
        } else {
          setUser(null);
          localStorage.removeItem('user');
        }
      } else if (response.status === 401) {
        console.log('🔐 Token invalid, clearing auth data...');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        setUser(null);
      } else {
        setUser(null);
        localStorage.removeItem('user');
      }
    } catch (error) {
      // Network error - keep cached user
      if (cachedUser) {
        try {
          setUser(JSON.parse(cachedUser));
        } catch (e) {
          setUser(null);
        }
      }
    }
  };

  const loadNotifications = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    
    try {
      const response = await fetch('/api/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      console.log('[v2.1.0] Notifications API response:', data);
      if (data.status === 'success' && data.data?.notifications) {
        // Store latest notifications (read + unread) and track unread count separately
        setNotifications(data.data.notifications.slice(0, 10));
        if (typeof data.data.unread_count === 'number') {
          console.log('[v2.1.0] Setting unread count:', data.data.unread_count);
          setUnreadCount(data.data.unread_count);
        }
      }
    } catch (error) {
      console.error('Notifications load error:', error);
    }
  };

  const loadUserCoins = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    
    try {
      const response = await fetch('/api/user-coins.php', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) return; // Silent fail for auth errors
      const data = await response.json();
      if (data.success) {
        setCoins(data.coins || 0);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const loadUserClub = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUserClub(null);
      return;
    }
    
    try {
      const response = await fetch('/api/clubs/', {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      // Debug removed
      // Debug removed
      
      if (data.success && data.clubs && data.clubs.length > 0) {
        // Find club where user is OWNER only
        const myOwnedClub = data.clubs.find(c => {
          const isOwner = c.role === 'owner';
          const isMember = c.is_member === true;
          return isOwner && isMember;
        });
        setUserClub(myOwnedClub || null);
      } else {
        setUserClub(null);
      }
    } catch (error) {
      // Silent fail
      setUserClub(null);
    }
  };

  const loadUserSubscription = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    
    try {
      const response = await fetch('/api/subscription/status.php', {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      // Debug removed
      
      if (data.status === 'success' && data.data?.subscription) {
        // Debug removed
        setSubscription(data.data.subscription);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      // Silent fail
      setSubscription(null);
    }
  };

  const markAllNotificationsRead = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    
    console.log('[v2.1.0] Mark all read called - will reload notifications');
    
    try {
      const response = await fetch('/api/notifications/mark-all-read', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        console.log('[v2.1.0] Mark all read success - reloading notifications');
        // Refresh notifications so list stays visible but badge resets
        loadNotifications();
        if (window.toast) {
          window.toast.success('All notifications marked as read');
        }
      }
    } catch (error) {
      console.error('Mark all read error:', error);
    }
  };

  // Get notification link based on type
  const getNotificationLink = (notif) => {
    if (notif.link) return notif.link;
    
    const notifData = notif.data ? JSON.parse(notif.data) : null;
    
    switch (notif.type) {
      case 'club_invite':
      case 'club_join_request':
      case 'club_join_accepted':
        return notifData?.club_id ? `/clubs/${notifData.club_id}` : '/clubs';
      case 'club_chat':
      case 'club_call':
        return notifData?.club_id ? `/clubs/room/${notifData.club_id}` : '/clubs';
      case 'stream_live':
      case 'watch_party':
        return notifData?.party_id ? `/watch-party/${notifData.party_id}` : '/watch-party';
      case 'new_follower':
      case 'follow':
        return notifData?.follower_id ? `/user/${notifData.follower_id}` : '/community';
      case 'like':
      case 'comment':
      case 'post_mention':
        return notifData?.post_id ? `/community?post=${notifData.post_id}` : '/community';
      case 'movie_recommendation':
        return notifData?.movie_id ? `/movie/${notifData.movie_id}` : '/movies';
      default:
        return '#';
    }
  };

  const handleNotificationAction = async (notificationId, action) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    
    // Prevent duplicate requests
    const actionKey = `${notificationId}-${action}`;
    if (processingActions.has(actionKey)) {
      console.log('Action already in progress, ignoring duplicate request');
      return;
    }
    
    try {
      setProcessingActions(prev => new Set(prev).add(actionKey));
      
      const response = await fetch(`/api/notifications/${notificationId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Mark notification as accepted/declined visually instead of removing
        setNotifications(prev => prev.map(n => {
          if (n.id === notificationId) {
            const notifData = n.data ? JSON.parse(n.data) : {};
            notifData.action_required = false;
            notifData.action_taken = action;
            return {
              ...n,
              is_read: 1,
              data: JSON.stringify(notifData)
            };
          }
          return n;
        }));
        
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Show success message
        if (window.toast) {
          window.toast.success(data.message || `Request ${action}ed successfully`);
        }
        
        // If accepted and has redirect URL, redirect immediately
        const redirectUrl = data.redirect || data.data?.redirect;
        if (action === 'accept' && redirectUrl) {
          window.location.href = redirectUrl;
        }
      } else {
        // Handle "already processed" errors silently by updating notification
        if (data.already_processed) {
          setNotifications(prev => prev.map(n => {
            if (n.id === notificationId) {
              const notifData = n.data ? JSON.parse(n.data) : {};
              notifData.action_required = false;
              return { ...n, is_read: 1, data: JSON.stringify(notifData) };
            }
            return n;
          }));
          setUnreadCount(prev => Math.max(0, prev - 1));
        } else {
          throw new Error(data.message || 'Action failed');
        }
      }
    } catch (error) {
      console.error('Notification action error:', error);
      // Check if it's an "already processed" error from catch block
      if (error.message && error.message.includes('already processed')) {
        // Silently remove notification
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        // Show error for other types of errors
        if (window.toast) {
          window.toast.error(error.message || 'Failed to process action');
        }
      }
    } finally {
      // Remove from processing set
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(actionKey);
        return newSet;
      });
    }
  };

  const handleLogout = async () => {
    try {
      // Call logout API to destroy server session
      await fetch('/api/logout.php', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout API error:', e);
    }
    
    // Clear ALL storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear ALL cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    // Force redirect to login
    window.location.href = '/login';
  };

  return (
    <header className="fixed top-0 left-0 right-0 w-full bg-black backdrop-blur-lg transition-all duration-300 border-b border-white/5" style={{ margin: 0, padding: 0, boxSizing: 'border-box', zIndex: 9999 }}>
      <nav className="w-full px-4 sm:px-6 lg:px-8 py-2 lg:py-3">
        <div className="flex items-center justify-between w-full gap-4 lg:gap-6">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0 h-8 sm:h-10 overflow-visible">
            <img src="/public/assets/images/cineclubs.png" alt="CineClubs Logo" className="h-10 sm:h-12 md:h-14 w-auto object-contain" />
            <div className="flex flex-col justify-center">
              <span className="font-black text-sm sm:text-base md:text-lg tracking-tight leading-tight"><span className="text-white">CINE</span><span className="text-red-600">CLUBS</span></span>
              <span className="text-white text-[7px] sm:text-[9px] md:text-[10px] font-bold uppercase tracking-wider">Your Private Cinema</span>
            </div>
          </a>
          
          {/* Navigation - All items always visible, icons only on smaller screens */}
          <ul className="hidden nav-1:flex items-center gap-2 lg:gap-3 nav-2:gap-4 3xl:gap-5 4xl:gap-6 font-semibold text-sm text-white flex-1 justify-center min-w-0">
            <li className="flex-shrink-0">
              <a href="/" className={`hover:text-red-600 transition-colors whitespace-nowrap flex items-center gap-1.5 lg:gap-2 ${currentPath === '/' || currentPath === '/home' ? activeClass : ''}`}>
                <i className="fas fa-home text-red-500"></i><span className="hidden 4xl:inline">{t.home}</span>
              </a>
            </li>
            <li className="flex-shrink-0">
              <a href="/live/streams" className={`hover:text-red-600 transition-colors whitespace-nowrap flex items-center gap-1.5 lg:gap-2 ${isActive('/live/streams') ? activeClass : ''}`}>
                <i className="fas fa-circle text-red-500 text-xs animate-pulse"></i><span className="hidden 4xl:inline">{t.liveStreams}</span>
              </a>
            </li>
            <li className="flex-shrink-0">
              <a href="/movies" className={`hover:text-red-600 transition-colors whitespace-nowrap flex items-center gap-1.5 lg:gap-2 ${isActive('/movies') ? activeClass : ''}`}>
                <i className="fas fa-film text-red-500"></i><span className="hidden 4xl:inline">{t.movies}</span>
              </a>
            </li>
            <li className="flex-shrink-0">
              <a href="/series" className={`hover:text-red-600 transition-colors whitespace-nowrap flex items-center gap-1.5 lg:gap-2 ${isActive('/series') ? activeClass : ''}`}>
                <i className="fas fa-tv text-red-500"></i><span className="hidden 4xl:inline">{t.series}</span>
              </a>
            </li>
            <li className="flex-shrink-0">
              <a href="/community" className={`hover:text-red-600 transition-colors whitespace-nowrap flex items-center gap-1.5 lg:gap-2 ${isActive('/community') ? activeClass : ''}`}>
                <i className="fas fa-comments text-red-500"></i><span className="hidden 4xl:inline">{t.community}</span>
              </a>
            </li>
          </ul>
          
          {/* Right Section - Search and User */}
          <div className="flex items-center gap-2 nav-1:gap-3 lg:gap-4 flex-shrink-0">
            {/* Language Switcher */}
            <div className="hidden nav-1:block relative">
              <button 
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white text-sm font-medium transition-colors"
              >
                <i className="fas fa-globe text-red-500"></i>
                <span className="uppercase">{lang}</span>
                <i className={`fas fa-chevron-down text-xs transition-transform ${showLangMenu ? 'rotate-180' : ''}`}></i>
              </button>
              
              {showLangMenu && (
                <div className="absolute right-0 top-full mt-2 bg-gray-900 border border-white/10 rounded-lg shadow-2xl z-50 overflow-hidden min-w-[120px]">
                  <button
                    onClick={() => { setLanguagePreference('en'); setShowLangMenu(false); }}
                    className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors flex items-center justify-between ${lang === 'en' ? 'bg-red-600/20 text-red-500' : 'text-white hover:bg-white/10'}`}
                  >
                    English
                    {lang === 'en' && <i className="fas fa-check text-red-500"></i>}
                  </button>
                  <button
                    onClick={() => { setLanguagePreference('ru'); setShowLangMenu(false); }}
                    className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors flex items-center justify-between ${lang === 'ru' ? 'bg-red-600/20 text-red-500' : 'text-white hover:bg-white/10'}`}
                  >
                    Русский
                    {lang === 'ru' && <i className="fas fa-check text-red-500"></i>}
                  </button>
                </div>
              )}
            </div>
            
            {/* Search Box - Flexible width, shrinks on smaller screens */}
            <div className="hidden nav-1:block relative w-32 lg:w-40 nav-2:w-44 3xl:w-48 4xl:w-56 flex-shrink min-w-0">
              <input 
                type="text" 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => search.trim().length >= 2 && setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                className="w-full px-4 py-2 pl-10 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-red-600/50"
                placeholder={t.search}
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-white/40"></i>
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-gray-900 border border-white/10 rounded-lg shadow-2xl z-50 max-h-96 overflow-y-auto">
                  {searchResults.map(movie => (
                    <a
                      key={movie.id}
                      href={`/movies/${movie.id}`}
                      className="flex items-center gap-3 p-2 hover:bg-white/10 transition-colors"
                    >
                      <img 
                        src={movie.thumbnail ? (movie.thumbnail.startsWith('/') ? movie.thumbnail : `/uploads/thumbnails/${movie.thumbnail}`) : '/placeholder.jpg'} 
                        alt={movie.title}
                        className="w-12 h-16 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{movie.title}</p>
                        <p className="text-white/60 text-xs truncate">{movie.genre?.split(',')[0] || 'Movie'}</p>
                      </div>
                    </a>
                  ))}
                </div>
              )}
              
              {showSearchResults && searchLoading && (
                <div className="absolute top-full mt-2 w-full bg-gray-900 border border-white/10 rounded-lg shadow-2xl z-50 p-4 text-center">
                  <i className="fas fa-spinner fa-spin text-white/60"></i>
                  <p className="text-white/60 text-xs mt-2">Searching...</p>
                </div>
              )}
            </div>
            
            {/* Desktop: Notifications - Only for logged in users */}
            {user && (
              <div className="hidden sm:block relative notifications-container">
                <button 
                  onClick={() => {
                    const newState = !showNotifications;
                    setShowNotifications(newState);
                    if (newState && unreadCount > 0) {
                      markAllNotificationsRead();
                    }
                  }}
                  className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <i className="fas fa-bell text-white"></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
              
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-gray-900 border border-white/10 rounded-lg shadow-2xl z-50">
                  <div className="p-4 border-b border-white/10 flex justify-between items-center">
                    <h3 className="text-white font-bold">🔔 {t.notifications}</h3>
                    {notifications.length > 0 && (
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          markAllNotificationsRead();
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          markAllNotificationsRead();
                        }}
                        className="text-xs text-red-500 hover:text-red-400 transition-colors active:text-red-600"
                      >
                        {t.markAllRead}
                      </button>
                    )}
                  </div>
                  <div className="p-4 max-h-[450px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-white/50">
                        <i className="fas fa-bell-slash text-2xl mb-2"></i>
                        <p className="text-sm">{t.noNotifications}</p>
                      </div>
                    ) : (
                      notifications.map(notif => {
                        const notifData = notif.data ? JSON.parse(notif.data) : null;
                        const hasAction = (notif.type === 'club_join_request' || notif.type === 'club_invite') && notifData?.action_required;
                        const actionTaken = notifData?.action_taken;
                        const isRead = notif.is_read === 1 || notif.is_read === true;
                        const isActiveCall = notif.type === 'club_call' && notifData?.call_active === true;
                        const isEndedCall = notif.type === 'club_call' && notifData?.call_active === false;
                        
                        // Active call - entire notification is clickable and goes to room
                        if (isActiveCall) {
                          return (
                            <a 
                              key={notif.id}
                              href={`/clubs/room/${notifData.club_id}?join_call=1`}
                              className="block p-2.5 hover:bg-green-800/40 rounded-lg mb-2 transition-colors bg-green-900/30 border border-green-500/30 cursor-pointer"
                              onClick={() => setShowNotifications(false)}
                            >
                              <div className="flex items-start gap-2">
                                <i className="fas fa-video text-green-500 animate-pulse mt-1 text-xs"></i>
                                <div className="flex-1">
                                  <p className="text-white text-xs font-semibold leading-tight">{notif.title}</p>
                                  <p className="text-green-400 text-xs mt-0.5 leading-tight font-medium">{notif.message}</p>
                                  <div className="mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded transition-colors">
                                    <i className="fas fa-video animate-pulse"></i>
                                    Join Call
                                  </div>
                                </div>
                              </div>
                            </a>
                          );
                        }
                        
                        // Ended call - not clickable
                        if (isEndedCall) {
                          return (
                            <div 
                              key={notif.id}
                              className="p-2.5 rounded-lg mb-2 bg-gray-800/30 opacity-60"
                            >
                              <div className="flex items-start gap-2">
                                <i className="fas fa-phone-slash text-gray-500 mt-1 text-xs"></i>
                                <div className="flex-1">
                                  <p className="text-white/60 text-xs font-semibold leading-tight">{notif.title}</p>
                                  <p className="text-gray-500 text-xs mt-0.5 leading-tight">{t.callEnded}</p>
                                  <p className="text-white/30 text-xs mt-1">
                                    {new Date(notif.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div 
                            key={notif.id} 
                            className={`p-2.5 hover:bg-white/10 rounded-lg mb-2 transition-colors ${isRead ? 'bg-white/5' : 'bg-red-900/20'}`}
                          >
                            <div className="flex items-start gap-2">
                              <i className={`${notif.icon || 'fa-bell'} ${['stream_live', 'club_invite', 'club_chat'].includes(notif.type) ? 'text-red-500' : 'text-white'} mt-1 text-xs`}></i>
                              <div className="flex-1">
                                <a 
                                  href={getNotificationLink(notif)} 
                                  className="block cursor-pointer"
                                  onClick={(e) => {
                                    const link = getNotificationLink(notif);
                                    if (link === '#') {
                                      e.preventDefault();
                                      return;
                                    }
                                    setShowNotifications(false);
                                    window.location.href = link;
                                  }}
                                >
                                  <p className="text-white text-xs font-semibold leading-tight">{notif.title}</p>
                                  <p className="text-white/70 text-xs mt-0.5 leading-tight">{notif.message}</p>
                                  <p className="text-white/40 text-xs mt-1">
                                    {new Date(notif.created_at).toLocaleDateString()}
                                  </p>
                                </a>
                                
                                {hasAction && (
                                  <div className="flex gap-2 mt-2">
                                    <button 
                                      onClick={() => handleNotificationAction(notif.id, 'accept')}
                                      disabled={processingActions.has(`${notif.id}-accept`) || processingActions.has(`${notif.id}-decline`)}
                                      className="flex-1 px-2 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                                    >
                                      {notif.type === 'club_invite' ? 'Join Club' : 'Accept'}
                                    </button>
                                    <button 
                                      onClick={() => handleNotificationAction(notif.id, 'decline')}
                                      disabled={processingActions.has(`${notif.id}-accept`) || processingActions.has(`${notif.id}-decline`)}
                                      className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                                    >
                                      Decline
                                    </button>
                                  </div>
                                )}
                                
                                {actionTaken && (
                                  <div className={`mt-2 px-2 py-1 rounded text-xs font-medium ${actionTaken === 'accept' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                                    <i className={`fas ${actionTaken === 'accept' ? 'fa-check-circle' : 'fa-times-circle'} mr-1`}></i>
                                    {actionTaken === 'accept' ? 'Joined' : 'Declined'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div className="border-t border-white/10 px-4 py-3 bg-black/30">
                    <p className="text-center text-white/40 text-xs">
                      <i className="fas fa-bell mr-1"></i>
                      {t.stayUpdated}
                    </p>
                  </div>
                </div>
              )}
              </div>
            )}
            
            {/* User Info */}
            {user ? (
              <div className="hidden sm:flex items-center gap-2 lg:gap-3 relative">
                {userClub && userClub.id && userClub.is_member === true && (
                  <a href={`/clubs/${userClub.id}`} className="hidden nav-1:flex items-center gap-2 px-2 lg:px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold transition-all shadow-lg">
                    <i className="fas fa-users"></i>
                    <span className="hidden 4xl:inline">{t.myClub}</span>
                  </a>
                )}
                <div className="relative user-menu-container">
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-600 to-pink-600 flex items-center justify-center text-white font-bold">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.username} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        user.username?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <span className="text-white font-medium">{user.username}</span>
                    <i className={`fas fa-chevron-down text-white/60 text-xs transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
                  </button>
                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-gradient-to-b from-gray-900 to-gray-950 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                      {/* Scrollable Content */}
                      <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                        {/* Coins & Subscribe */}
                        <div className="p-3 border-b border-white/5">
                          <div className="flex gap-2">
                            <a href="/buy-coins" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-black/50 border border-red-500/50 hover:border-red-500 hover:bg-red-600/10 rounded-lg text-white transition-all group">
                              <img src="/cinebadge.png" alt="Coins" className="w-5 h-5 object-contain" />
                              <span className="font-bold text-red-400 group-hover:text-red-300">{Math.floor(parseFloat(user?.coin_balance || 0)).toLocaleString()}</span>
                            </a>
                            <a href="/subscribe" className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-white transition-all ${subscription ? 'bg-gradient-to-r from-red-600/20 to-red-800/20 border border-red-600/50 hover:border-red-500' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600'}`}>
                              <img src="/whitelogo.png" alt="Subscribe" className="w-5 h-5 object-contain" />
                              <span className="font-bold text-sm">{subscription?.plan_name || t.subscribe}</span>
                            </a>
                          </div>
                        </div>
                        
                        {/* Navigation Section */}
                        <div className="p-2 border-b border-white/5">
                          <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Navigation</p>
                          <a href="/" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${currentPath === '/' || currentPath === '/home' ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                            <i className="fas fa-home w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.home}</span>
                          </a>
                          <a href="/live/streams" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/live/streams') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                            <i className="fas fa-broadcast-tower w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.liveStreams}</span>
                            <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                          </a>
                          <a href="/movies" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/movies') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                            <i className="fas fa-film w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.movies}</span>
                          </a>
                          <a href="/series" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/series') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                            <i className="fas fa-tv w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.series}</span>
                          </a>
                          <a href="/community" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/community') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                            <i className="fas fa-comments w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.community}</span>
                          </a>
                        </div>
                        
                        {/* Account Section */}
                        <div className="p-2 border-b border-white/5">
                          <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">{t.profile}</p>
                          <a href="/profile" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                            <i className="fas fa-user w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.profile}</span>
                          </a>
                          <a href="/settings" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                            <i className="fas fa-cog w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.settings}</span>
                          </a>
                        </div>
                        
                        {/* Features Section */}
                        <div className="p-2 border-b border-white/5">
                          <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Features</p>
                          <a href="/watch-party" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                            <i className="fas fa-video w-5 text-center text-red-500"></i>
                            <span className="font-medium">{t.watchParty}</span>
                          </a>
                          {userClub && userClub.id && userClub.is_member === true ? (
                            <a href={`/clubs/${userClub.id}`} className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                              <i className="fas fa-users w-5 text-center text-red-500"></i>
                              <span className="font-medium">{t.myClub}</span>
                            </a>
                          ) : null}
                          {user.is_streamer && user.stream_slug ? (
                            <a href={`/live/streamer/${user.stream_slug}`} className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                              <i className="fas fa-satellite-dish w-5 text-center text-red-500"></i>
                              <span className="font-medium">{t.myStreamRoom}</span>
                            </a>
                          ) : null}
                        </div>
                        
                        {/* Admin Section */}
                        {user.role === 'admin' && (
                          <div className="p-2 border-b border-white/5">
                            <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Admin</p>
                            <a href="/admin" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                              <i className="fas fa-shield-alt w-5 text-center text-red-500"></i>
                              <span className="font-medium">{t.adminPanel}</span>
                            </a>
                          </div>
                        )}
                      </div>
                      
                      {/* Logout - Fixed at bottom */}
                      <div className="p-2 bg-black/30 border-t border-white/5">
                        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-400 hover:text-white hover:bg-red-600 rounded-lg transition-all font-medium">
                          <i className="fas fa-sign-out-alt"></i>
                          <span>{t.logout}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <a href="/login" className="px-4 py-2 text-white hover:text-red-600 transition-colors font-medium">{t.login}</a>
                <a href="/register" className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold transition-all">{t.signUp}</a>
              </div>
            )}
            
            {/* Mobile: Notification Bell */}
            {user && (
              <div className="sm:hidden relative notifications-container">
                <button 
                  onClick={() => {
                    const newState = !showNotifications;
                    setShowNotifications(newState);
                    if (newState && unreadCount > 0) {
                      markAllNotificationsRead();
                    }
                  }}
                  className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <i className="fas fa-bell text-white"></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Mobile Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-gray-900 border border-white/10 rounded-lg shadow-2xl z-50">
                    <div className="p-3 border-b border-white/10 flex justify-between items-center">
                      <h3 className="text-white font-bold text-sm">🔔 {t.notifications}</h3>
                      {notifications.length > 0 && (
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            markAllNotificationsRead();
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            markAllNotificationsRead();
                          }}
                          className="text-xs text-red-500 hover:text-red-400 transition-colors active:text-red-600"
                        >
                          {t.markAllRead}
                        </button>
                      )}
                    </div>
                    <div className="p-3 max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="text-center py-6 text-white/50">
                          <i className="fas fa-bell-slash text-xl mb-2"></i>
                          <p className="text-sm">{t.noNotifications}</p>
                        </div>
                      ) : (
                        notifications.map(notif => {
                          const notifData = notif.data ? JSON.parse(notif.data) : null;
                          const hasAction = (notif.type === 'club_join_request' || notif.type === 'club_invite') && notifData?.action_required;
                          const actionTaken = notifData?.action_taken;
                          const isRead = notif.is_read === 1 || notif.is_read === true;
                          const isActiveCall = notif.type === 'club_call' && notifData?.call_active === true;
                          const isEndedCall = notif.type === 'club_call' && notifData?.call_active === false;
                          
                          // Active call - entire notification clickable
                          if (isActiveCall) {
                            return (
                              <a 
                                key={notif.id}
                                href={`/clubs/room/${notifData.club_id}?join_call=1`}
                                className="block p-2 hover:bg-green-800/40 rounded-lg text-xs mb-2 bg-green-900/30 border border-green-500/30 cursor-pointer"
                                onClick={() => setShowNotifications(false)}
                              >
                                <div className="flex items-start gap-2">
                                  <i className="fas fa-video text-green-500 animate-pulse mt-0.5"></i>
                                  <div className="flex-1">
                                    <p className="text-white font-semibold leading-tight">{notif.title}</p>
                                    <p className="text-green-400 mt-0.5 leading-tight font-medium">{notif.message}</p>
                                    <div className="mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-xs font-bold rounded">
                                      <i className="fas fa-video animate-pulse"></i>
                                      Join Call
                                    </div>
                                  </div>
                                </div>
                              </a>
                            );
                          }
                          
                          // Ended call - not clickable
                          if (isEndedCall) {
                            return (
                              <div 
                                key={notif.id}
                                className="p-2 rounded-lg text-xs mb-2 bg-gray-800/30 opacity-60"
                              >
                                <div className="flex items-start gap-2">
                                  <i className="fas fa-phone-slash text-gray-500 mt-0.5"></i>
                                  <div className="flex-1">
                                    <p className="text-white/60 font-semibold leading-tight">{notif.title}</p>
                                    <p className="text-gray-500 mt-0.5 leading-tight">{t.callEnded}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <div 
                              key={notif.id} 
                              className={`p-2 hover:bg-white/10 rounded-lg text-xs transition-colors mb-2 ${isRead ? 'bg-white/5' : 'bg-red-900/20'}`}
                            >
                              <a 
                                href={getNotificationLink(notif)} 
                                className="block cursor-pointer"
                                onClick={(e) => {
                                  const link = getNotificationLink(notif);
                                  if (link === '#') {
                                    e.preventDefault();
                                    return;
                                  }
                                  setShowNotifications(false);
                                  window.location.href = link;
                                }}
                              >
                                <p className="text-white font-semibold leading-tight">{notif.title}</p>
                                <p className="text-white/70 mt-0.5 leading-tight">{notif.message}</p>
                                <p className="text-white/40 text-xs mt-1">
                                  {new Date(notif.created_at).toLocaleDateString()}
                                </p>
                              </a>
                              
                              {hasAction && (
                                <div className="flex gap-2 mt-2">
                                  <button 
                                    onClick={() => handleNotificationAction(notif.id, 'accept')}
                                    disabled={processingActions.has(`${notif.id}-accept`) || processingActions.has(`${notif.id}-decline`)}
                                    className="flex-1 px-2 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                                  >
                                    {notif.type === 'club_invite' ? 'Join Club' : 'Accept'}
                                  </button>
                                  <button 
                                    onClick={() => handleNotificationAction(notif.id, 'decline')}
                                    disabled={processingActions.has(`${notif.id}-accept`) || processingActions.has(`${notif.id}-decline`)}
                                    className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                                  >
                                    Decline
                                  </button>
                                </div>
                              )}
                              
                              {actionTaken && (
                                <div className={`mt-2 px-2 py-1 rounded text-xs font-medium ${actionTaken === 'accept' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                                  <i className={`fas ${actionTaken === 'accept' ? 'fa-check-circle' : 'fa-times-circle'} mr-1`}></i>
                                  {actionTaken === 'accept' ? 'Joined' : 'Declined'}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="border-t border-white/10 px-3 py-2 bg-black/30">
                      <p className="text-center text-white/40 text-xs">
                        <i className="fas fa-bell mr-1"></i>
                        {t.stayUpdated}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className="nav-1:hidden text-white p-1.5 sm:p-2 hover:bg-white/10 rounded transition-colors border border-white/20"
            >
              <i className={`fas ${isOpen ? 'fa-times' : 'fa-bars'} text-base sm:text-lg`}></i>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="nav-1:hidden border-t border-white/10 bg-gradient-to-b from-gray-900 to-gray-950"
            style={{ overscrollBehavior: 'contain' }}
          >
            {user ? (
              <>
                {/* User Header */}
                <div className="p-4 bg-gradient-to-r from-red-600/20 to-transparent border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-pink-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.username} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        user.username?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold truncate">{user.username}</p>
                      <p className="text-white/50 text-xs truncate">{user.email}</p>
                    </div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                  {/* Coins & Subscribe */}
                  <div className="p-3 border-b border-white/5">
                    <div className="flex gap-2">
                      <a href="/buy-coins" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-black/50 border border-red-500/50 hover:border-red-500 hover:bg-red-600/10 rounded-lg text-white transition-all group">
                        <img src="/cinebadge.png" alt="Coins" className="w-5 h-5 object-contain" />
                        <span className="font-bold text-red-400 group-hover:text-red-300">{Math.floor(parseFloat(user?.coin_balance || 0)).toLocaleString()}</span>
                      </a>
                      <a href="/subscribe" className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-white transition-all ${subscription ? 'bg-gradient-to-r from-red-600/20 to-red-800/20 border border-red-600/50 hover:border-red-500' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600'}`}>
                        <img src="/whitelogo.png" alt="Subscribe" className="w-5 h-5 object-contain" />
                        <span className="font-bold text-sm">{subscription?.plan_name || t.subscribe}</span>
                      </a>
                    </div>
                  </div>
                  
                  {/* Navigation Section */}
                  <div className="p-2 border-b border-white/5">
                    <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Navigation</p>
                    <a href="/" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${currentPath === '/' || currentPath === '/home' ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                      <i className="fas fa-home w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.home}</span>
                    </a>
                    <a href="/live/streams" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/live/streams') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                      <i className="fas fa-broadcast-tower w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.liveStreams}</span>
                      <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    </a>
                    <a href="/movies" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/movies') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                      <i className="fas fa-film w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.movies}</span>
                    </a>
                    <a href="/series" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/series') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                      <i className="fas fa-tv w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.series}</span>
                    </a>
                    <a href="/community" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/community') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                      <i className="fas fa-comments w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.community}</span>
                    </a>
                    <a href="/clubs" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/clubs') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                      <i className="fas fa-users w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.clubs}</span>
                    </a>
                  </div>
                  
                  {/* Account Section */}
                  <div className="p-2 border-b border-white/5">
                    <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">{t.profile}</p>
                    <a href="/profile" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                      <i className="fas fa-user w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.profile}</span>
                    </a>
                    <a href="/settings" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                      <i className="fas fa-cog w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.settings}</span>
                    </a>
                  </div>
                  
                  {/* Features Section */}
                  <div className="p-2 border-b border-white/5">
                    <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Features</p>
                    <a href="/watch-party" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                      <i className="fas fa-video w-5 text-center text-red-500"></i>
                      <span className="font-medium">{t.watchParty}</span>
                    </a>
                    {userClub && userClub.id && userClub.is_member === true ? (
                      <a href={`/clubs/${userClub.id}`} className="flex items-center gap-3 px-3 py-2.5 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all">
                        <i className="fas fa-users w-5 text-center text-red-500"></i>
                        <span className="font-medium">{t.myClub}</span>
                      </a>
                    ) : null}
                    {user.is_streamer && user.stream_slug ? (
                      <a href={`/live/streamer/${user.stream_slug}`} className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                        <i className="fas fa-satellite-dish w-5 text-center text-red-500"></i>
                        <span className="font-medium">{t.myStreamRoom}</span>
                      </a>
                    ) : null}
                  </div>
                  
                  {/* Admin Section */}
                  {user.role === 'admin' && (
                    <div className="p-2 border-b border-white/5">
                      <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Admin</p>
                      <a href="/admin" className="flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white rounded-lg transition-all">
                        <i className="fas fa-shield-alt w-5 text-center text-red-500"></i>
                        <span className="font-medium">{t.adminPanel}</span>
                      </a>
                    </div>
                  )}
                  
                  {/* Language Switcher */}
                  <div className="p-3 border-b border-white/5">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60 text-sm flex items-center gap-2">
                        <i className="fas fa-globe text-red-500"></i>
                        {t.language}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setLanguagePreference('en')}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${lang === 'en' ? 'bg-red-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                        >
                          EN
                        </button>
                        <button
                          onClick={() => setLanguagePreference('ru')}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${lang === 'ru' ? 'bg-red-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                        >
                          RU
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Logout - Fixed at bottom */}
                <div className="p-2 pb-6 bg-black/30 border-t border-white/5">
                  <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-400 hover:text-white hover:bg-red-600 rounded-lg transition-all font-medium text-sm">
                    <i className="fas fa-sign-out-alt"></i>
                    <span>{t.logout}</span>
                  </button>
                </div>
              </>
            ) : (
              /* Guest Menu */
              <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                {/* Navigation Section */}
                <div className="p-2 border-b border-white/5">
                  <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Navigation</p>
                  <a href="/" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${currentPath === '/' || currentPath === '/home' ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                    <i className="fas fa-home w-5 text-center text-red-500"></i>
                    <span className="font-medium">{t.home}</span>
                  </a>
                  <a href="/live/streams" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/live/streams') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                    <i className="fas fa-broadcast-tower w-5 text-center text-red-500"></i>
                    <span className="font-medium">{t.liveStreams}</span>
                    <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  </a>
                  <a href="/movies" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/movies') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                    <i className="fas fa-film w-5 text-center text-red-500"></i>
                    <span className="font-medium">{t.movies}</span>
                  </a>
                  <a href="/series" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/series') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                    <i className="fas fa-tv w-5 text-center text-red-500"></i>
                    <span className="font-medium">{t.series}</span>
                  </a>
                  <a href="/community" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/community') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                    <i className="fas fa-comments w-5 text-center text-red-500"></i>
                    <span className="font-medium">{t.community}</span>
                  </a>
                  <a href="/clubs" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive('/clubs') ? 'bg-red-600/20 text-red-400' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                    <i className="fas fa-users w-5 text-center text-red-500"></i>
                    <span className="font-medium">{t.clubs}</span>
                  </a>
                </div>
                
                {/* Language Switcher */}
                <div className="p-3 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-sm flex items-center gap-2">
                      <i className="fas fa-globe text-red-500"></i>
                      {t.language}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setLanguagePreference('en')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${lang === 'en' ? 'bg-red-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                      >
                        EN
                      </button>
                      <button
                        onClick={() => setLanguagePreference('ru')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${lang === 'ru' ? 'bg-red-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                      >
                        RU
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Login/Register */}
                <div className="p-3 bg-black/30 border-t border-white/5">
                  <div className="flex gap-2">
                    <a href="/login" className="flex-1 px-4 py-3 text-center text-white hover:bg-white/10 rounded-lg transition-colors font-medium border border-white/20">{t.login}</a>
                    <a href="/register" className="flex-1 px-4 py-3 text-center bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 rounded-lg text-white font-semibold transition-all">{t.signUp}</a>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </nav>
    </header>
  );
};

export default UniversalNavbar;
